import { ApiError, type BulkMessageCustomersResult } from '@mevaser/shared'
import {
  Alert,
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Textarea,
  useToast,
} from '@mevaser/ui'
import { useMutation } from '@tanstack/react-query'
import { useState } from 'react'
import { api } from '../lib/api.js'

const MAX_BODY_LENGTH = 640

export interface BulkMessageCustomer {
  id: string
  name: string
  /** Preview only — the server re-checks eligibility at send time; see below. */
  smsReachable: boolean
}

/**
 * Composes one text and sends it as a separate, individual SMS to every
 * eligible selected customer (t0914) — never a group text, and no recipient
 * ever sees another. `smsReachable` here is a preview taken from the list row
 * so the compose screen can show who will be skipped before sending; the
 * request still includes every selected id, not just the ones that looked
 * eligible — the server is the authority, and its skipped/failed report in
 * the result step is the actual record.
 */
export function CustomerBulkMessageDialog({
  open,
  onClose,
  customers,
  onSent,
}: {
  open: boolean
  onClose: () => void
  customers: BulkMessageCustomer[]
  onSent: (sentIds: string[]) => void
}) {
  const toast = useToast()
  const [body, setBody] = useState('')

  const eligible = customers.filter((c) => c.smsReachable)
  const ineligible = customers.filter((c) => !c.smsReachable)

  const send = useMutation({
    mutationFn: () =>
      api.post<BulkMessageCustomersResult>('/customers/bulk-message', {
        customerIds: customers.map((c) => c.id),
        body: body.trim(),
      }),
    onError: (error: Error) =>
      toast.error('Could not send the text', error instanceof ApiError ? error.message : undefined),
  })

  const result = send.data

  const handleClose = () => {
    if (result) onSent(result.sent)
    onClose()
    setBody('')
    send.reset()
  }

  return (
    <Dialog open={open} onOpenChange={(next) => !next && handleClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {result
              ? 'Text sent'
              : `Text ${customers.length} customer${customers.length === 1 ? '' : 's'}`}
          </DialogTitle>
        </DialogHeader>

        {result ? (
          <DialogBody className="space-y-3">
            <p className="text-sm">
              Sent to {result.sent.length} customer{result.sent.length === 1 ? '' : 's'}.
            </p>
            {result.skipped.length > 0 ? (
              <ResultList title="Could not receive it" items={result.skipped} />
            ) : null}
            {result.failed.length > 0 ? (
              <ResultList title="Send failed" items={result.failed} />
            ) : null}
          </DialogBody>
        ) : (
          <DialogBody className="space-y-4">
            <p className="text-sm text-foreground-muted">
              Sends one individual text to each of the {eligible.length} selected customer
              {eligible.length === 1 ? '' : 's'} who can receive texts. No recipient sees who else
              got it.
            </p>
            {ineligible.length > 0 ? (
              <Alert tone="warning">
                {ineligible.length} will be skipped (no text-capable phone):{' '}
                {ineligible.map((c) => c.name).join(', ')}
              </Alert>
            ) : null}
            <Textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              maxLength={MAX_BODY_LENGTH}
              rows={4}
              placeholder="Message"
              autoFocus
            />
            <p className="text-end text-xs text-foreground-muted tabular">
              {body.length}/{MAX_BODY_LENGTH}
            </p>
          </DialogBody>
        )}

        <DialogFooter>
          {result ? (
            <Button variant="primary" onClick={handleClose}>
              Done
            </Button>
          ) : (
            <>
              <Button variant="ghost" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                variant="primary"
                disabled={!body.trim() || eligible.length === 0 || send.isPending}
                onClick={() => send.mutate()}
              >
                {send.isPending
                  ? 'Sending…'
                  : `Send to ${eligible.length} customer${eligible.length === 1 ? '' : 's'}`}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function ResultList({
  title,
  items,
}: {
  title: string
  items: { customerId: string; name: string | null; reason: string }[]
}) {
  return (
    <div>
      <p className="text-2xs font-medium uppercase tracking-wide text-foreground-muted">{title}</p>
      <ul className="mt-1 space-y-0.5 text-sm">
        {items.map((item) => (
          <li key={item.customerId} className="text-foreground-muted">
            {item.name ?? 'Deleted customer'} — {item.reason}
          </li>
        ))}
      </ul>
    </div>
  )
}
